import { type ReactNode } from 'react';

import AutoSizer from '@/components/AutoSizer';
import { Empty } from '@/components/Empty';
import WidgetError from '@/components/errors/WidgetError';
import { SpinnerOverlay } from '@/components/loaders';
import type SolAPIError from '@/error/SolAPIError';
import { cn } from '@/lib/utils';

import SimpleSankeyDiagram from './SimpleSankeyDiagram';
import SimpleSankeyDiagramSkeleton from './SimpleSankeyDiagramSkeleton';
import type { SankeyLink, SankeyNode, SimpleSankeyDiagramProps } from './types';

type Props = Omit<SimpleSankeyDiagramProps, 'width' | 'height' | 'nodes' | 'links'> & {
  isLoading?: boolean;
  error?: SolAPIError | null;
  nodes: SankeyNode[] | undefined;
  links: SankeyLink[] | undefined;
  emptyMessage: ReactNode;
};

const SimpleSankeyDiagramContainer = ({
  nodes,
  links,
  isLoading,
  error,
  emptyMessage,
  ...otherProps
}: Props) => {
  if (error) {
    return <WidgetError />;
  }

  return (
    <div className={cn('size-full overflow-x-auto overflow-y-hidden [&>div]:size-full!')}>
      <AutoSizer>
        {({ width, height }) => {
          if (isLoading && !nodes) {
            return <SimpleSankeyDiagramSkeleton width={width} height={height} />;
          }

          if (!nodes || nodes?.length === 0) {
            return <Empty>{emptyMessage}</Empty>;
          }

          return (
            <SpinnerOverlay isSpinning={!!isLoading && !error}>
              <SimpleSankeyDiagram
                nodes={nodes ?? []}
                links={links ?? []}
                width={width}
                height={height}
                {...otherProps}
              />
            </SpinnerOverlay>
          );
        }}
      </AutoSizer>
    </div>
  );
};

export default SimpleSankeyDiagramContainer;
